import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const STAGE_CONFIG = {
  'Pending Team Assignment': {
    icon: 'Users',
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    accentBg: 'bg-warning',
    actionIcon: 'UserPlus',
    actionLabel: 'Assign Engineer / Team',
  },
  'Calculated': {
    icon: 'Calculator',
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    border: 'border-blue-400/30',
    accentBg: 'bg-blue-400',
    actionIcon: 'CheckSquare',
    actionLabel: 'Review & Confirm Calculation',
  },
  'Pending Approval': {
    icon: 'Clock',
    color: 'text-orange-400',
    bg: 'bg-orange-400/10',
    border: 'border-orange-400/30',
    accentBg: 'bg-orange-400',
    actionIcon: 'ShieldCheck',
    actionLabel: 'Approve / Reject',
  },
  'Approved': {
    icon: 'CheckCircle',
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
    accentBg: 'bg-success',
    actionIcon: 'CreditCard',
    actionLabel: 'Prepare Payment',
  },
  'Ready for Payment': {
    icon: 'CreditCard',
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/30',
    accentBg: 'bg-purple-400',
    actionIcon: 'BadgeCheck',
    actionLabel: 'Confirm & Execute Payment',
  },
  'Paid': {
    icon: 'Banknote',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30',
    accentBg: 'bg-emerald-400',
    actionIcon: 'FileText',
    actionLabel: 'View Receipt',
  },
  'Rejected': {
    icon: 'XCircle',
    color: 'text-error',
    bg: 'bg-error/10',
    border: 'border-error/30',
    accentBg: 'bg-error',
    actionIcon: 'RotateCcw',
    actionLabel: 'View Details',
  },
  'On Hold': {
    icon: 'PauseCircle',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/30',
    accentBg: 'bg-yellow-400',
    actionIcon: 'Eye',
    actionLabel: 'View Details',
  },
};

const DEFAULT_STAGE = {
  icon: 'Bell',
  color: 'text-primary',
  bg: 'bg-primary/10',
  border: 'border-primary/30',
  accentBg: 'bg-primary',
  actionIcon: 'ArrowRight',
  actionLabel: 'View Details',
};

/**
 * Real-time workflow notification popup.
 * Appears immediately when a new notification is assigned to the user.
 * Stays visible until dismissed or actioned.
 */
const RealtimeNotificationPopup = ({ notification, onAction, onDismiss }) => {
  const navigate = useNavigate();

  if (!notification) return null;

  const cfg = STAGE_CONFIG?.[notification?.stage] || DEFAULT_STAGE;

  const handleAction = () => {
    onAction?.();
    if (notification?.action_url) {
      navigate(notification?.action_url);
    } else {
      navigate('/notification-history');
    }
  };

  const handleDismiss = () => {
    onDismiss?.();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className={`relative w-full max-w-md bg-card border ${cfg?.border} rounded-2xl shadow-2xl overflow-hidden`}>
        {/* Accent top bar */}
        <div className={`h-1 w-full ${cfg?.accentBg}`} />

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-11 h-11 rounded-xl ${cfg?.bg}`}>
              <Icon name={cfg?.icon} size={22} className={cfg?.color} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${cfg?.bg} ${cfg?.color}`}>
                  {notification?.stage}
                </span>
                {notification?.ticket_number && (
                  <span className="text-[10px] font-mono font-bold text-foreground bg-muted px-2 py-0.5 rounded-lg">
                    {notification?.ticket_number}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">New workflow task assigned to you</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className={`mx-5 mb-4 p-4 rounded-xl ${cfg?.bg} border ${cfg?.border}`}>
          {notification?.project_name && (
            <p className="text-xs font-semibold text-foreground mb-1.5 truncate">
              📁 {notification?.project_name}
            </p>
          )}
          <p className="text-sm text-foreground/90 leading-relaxed">
            {notification?.message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-5 pb-5">
          <button
            onClick={handleAction}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl ${cfg?.bg} border ${cfg?.border} text-sm font-semibold ${cfg?.color} hover:opacity-80 transition-opacity`}
          >
            <Icon name={cfg?.actionIcon} size={15} />
            {notification?.action_label || cfg?.actionLabel}
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-2.5 rounded-xl bg-muted border border-border text-sm font-medium text-muted-foreground hover:bg-muted/80 transition-colors"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default RealtimeNotificationPopup;
